import type { AgentWeekInput, SetEntry, TodayPayload, WorkoutSession } from "./shared";

export class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init.headers }
  });
  const payload = await response.json().catch(() => ({ error: "Unexpected server response" })) as { error?: string } & T;
  if (!response.ok) throw new ApiError(payload.error ?? `Request failed (${response.status})`, response.status);
  return payload;
}

export type PlanPayload = {
  weeks: Array<{ id: string; week_start: string; title: string; focus: string | null; coach_notes: string | null; warmup: string[]; source: string }>;
  days: Array<{ id: string; routine_week_id: string; day_date: string; title: string; focus: string | null; estimated_minutes: number | null; position: number; session_status: "planned" | "active" | "completed" }>;
  blocks: Array<{ id: string; routine_day_id: string; label: string; kind: string; rounds: number; rest_seconds: number; notes: string | null; position: number; exercise_names: string }>;
};

export type ReportsPayload = {
  summary: { sessions: number; sets: number; volume: number; avg_effort: number };
  weekly: Array<{ week: string; sessions: number; sets: number; volume: number }>;
  exercises: Array<{ id: string; name: string; sets: number; best_weight: number; estimated_1rm: number; last_trained: string }>;
  recent: Array<{ id: string; title: string; started_at: string; completed_at: string; perceived_effort: number | null; sets: number; volume: number }>;
};

export type PlanDraft = {
  id: string;
  week_start: string;
  title: string;
  status: "draft" | "published" | "archived";
  created_at: string;
  updated_at: string;
  published_at: string | null;
  payload: AgentWeekInput;
};

export type ApiToken = {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
};

export type AccountPayload = {
  ok: true;
  role: "user" | "agent";
  auth_provider: "better-auth" | "agent-token";
  username: string;
  user: { id: string; name: string; email: string };
  athlete: {
    id: string;
    membership_role: "owner" | "coach" | "viewer";
    display_name: string;
    timezone: string;
    unit_system: "lb" | "kg";
  };
  onboarding: {
    completed: boolean;
    completed_at: string | null;
    training_days_per_week: number | null;
    session_minutes: number | null;
    experience_level: "beginner" | "intermediate" | "advanced" | null;
    goals: string;
    available_equipment: string;
    movement_constraints: string;
    variety_preference: "steady" | "balanced" | "frequent";
  };
};

export type OnboardingInput = {
  display_name: string;
  timezone: string;
  unit_system: "lb" | "kg";
  training_days_per_week: number;
  session_minutes: number;
  experience_level: "beginner" | "intermediate" | "advanced";
  goals: string;
  available_equipment: string;
  movement_constraints: string;
  variety_preference: "steady" | "balanced" | "frequent";
};

export const api = {
  check: () => request<AccountPayload>("/api/me"),
  saveOnboarding: (input: OnboardingInput) => request<AccountPayload>("/api/onboarding", { method: "PUT", body: JSON.stringify(input) }),
  today: () => {
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    return request<TodayPayload>(`/api/today?date=${localDate}`);
  },
  startSession: (routineDayId: string) => request<{ session: WorkoutSession }>("/api/sessions", { method: "POST", body: JSON.stringify({ routine_day_id: routineDayId }) }),
  logSet: (sessionId: string, value: { routine_item_id: string; exercise_id: string; round_number: number; weight?: number; reps?: number; duration_seconds?: number; rir?: number }) =>
    request<{ set: SetEntry }>(`/api/sessions/${sessionId}/sets`, { method: "POST", body: JSON.stringify(value) }),
  completeSession: (sessionId: string, value: { perceived_effort: number; notes?: string }) =>
    request<{ session: WorkoutSession }>(`/api/sessions/${sessionId}`, { method: "PATCH", body: JSON.stringify({ status: "completed", ...value }) }),
  plan: () => request<PlanPayload>("/api/plan"),
  week: (weekId: string) => request<{ week: AgentWeekInput }>(`/api/plan/weeks/${encodeURIComponent(weekId)}`),
  drafts: () => request<{ drafts: PlanDraft[] }>("/api/plan/drafts"),
  createDraft: (week: AgentWeekInput) => request<{ draft: PlanDraft }>("/api/plan/drafts", { method: "POST", body: JSON.stringify(week) }),
  updateDraft: (draftId: string, week: AgentWeekInput) => request<{ draft: PlanDraft }>(`/api/plan/drafts/${encodeURIComponent(draftId)}`, { method: "PUT", body: JSON.stringify(week) }),
  publishDraft: (draftId: string) => request<{ ok: true; week_id: string }>(`/api/plan/drafts/${encodeURIComponent(draftId)}/publish`, { method: "POST" }),
  tokens: () => request<{ tokens: ApiToken[] }>("/api/tokens"),
  createToken: (name: string, scopes: string[]) => request<{ token: string; credential: ApiToken }>("/api/tokens", { method: "POST", body: JSON.stringify({ name, scopes }) }),
  revokeToken: (tokenId: string) => request<{ ok: true }>(`/api/tokens/${encodeURIComponent(tokenId)}`, { method: "DELETE" }),
  reports: () => request<ReportsPayload>("/api/reports")
};
